import { CartErrorEvent, CartUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class CompleteSetBundle extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-complete-set-row]'));
    this.tiers = Array.from(this.querySelectorAll('[data-complete-set-tier]')).map((tier) => ({
      element: tier,
      message: tier.querySelector('[data-complete-set-tier-message]'),
      threshold: Number(tier.dataset.threshold || 0),
      discount: Number(tier.dataset.discount || 0),
    }));

    this.elements = {
      addButton: this.querySelector('[data-complete-set-add]'),
      count: this.querySelector('[data-complete-set-count]'),
      unlock: this.querySelector('[data-complete-set-unlock]'),
      subtotalLabel: this.querySelector('[data-complete-set-subtotal-label]'),
      subtotal: this.querySelector('[data-complete-set-subtotal]'),
      discountLabel: this.querySelector('[data-complete-set-discount-label]'),
      discount: this.querySelector('[data-complete-set-discount]'),
      save: this.querySelector('[data-complete-set-save]'),
      total: this.querySelector('[data-complete-set-total]'),
      next: this.querySelector('[data-complete-set-next]'),
      nextTitle: this.querySelector('[data-complete-set-next-title]'),
      nextDiscount: this.querySelector('[data-complete-set-next-discount]'),
      nextSave: this.querySelector('[data-complete-set-next-save]'),
      nextImage: this.querySelector('[data-complete-set-next-image]'),
      error: this.querySelector('[data-complete-set-error]'),
    };

    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currencyCode = this.dataset.currencyCode || 'USD';
    this.buttonLabel = this.dataset.buttonLabel || 'Add selected items to cart';
    this.addingLabel = this.dataset.addingLabel || 'Adding...';
    this.addedLabel = this.dataset.addedLabel || 'Added';
    this.cartVariantIds = new Set();
    this.cartLineKeysByVariantId = new Map();

    this.rows.forEach((row) => {
      const checkbox = row.querySelector('[data-complete-set-checkbox]');

      row.dataset.defaultChecked = checkbox?.checked ? 'true' : 'false';

      checkbox?.addEventListener('change', this.handleSelectionChange);
      row.querySelector('[data-complete-set-variant-select]')?.addEventListener('change', this.handleVariantChange);
    });

    this.elements.addButton?.addEventListener('click', this.handleAddToCart);
    this.update();
    this.hydrateFromCart();
  }

  disconnectedCallback() {
    this.rows.forEach((row) => {
      row.querySelector('[data-complete-set-checkbox]')?.removeEventListener('change', this.handleSelectionChange);
      row.querySelector('[data-complete-set-variant-select]')?.removeEventListener('change', this.handleVariantChange);
    });

    this.elements.addButton?.removeEventListener('click', this.handleAddToCart);
  }

  handleSelectionChange = (event) => {
    const row = event.currentTarget?.closest('[data-complete-set-row]');

    if (row) {
      row.dataset.userTouched = 'true';
    }

    this.clearError();
    this.update();
  };

  handleVariantChange = (event) => {
    const select = event.currentTarget;
    const row = select.closest('[data-complete-set-row]');
    const selectedOption = select.selectedOptions?.[0];

    if (!row || !selectedOption) return;

    this.applyVariantOption(row, selectedOption);
    this.syncRowCheckboxWithCart(row, { preserveUserSelection: true });

    this.clearError();
    this.update();
  };

  handleAddToCart = async () => {
    const selectedRows = this.selectedRows;
    const selectedVariantIds = new Set(selectedRows.map((row) => String(row.dataset.variantId || '')));
    const itemsToAdd = selectedRows
      .filter((row) => row.dataset.available === 'true')
      .filter((row) => !this.cartVariantIds.has(String(row.dataset.variantId || '')))
      .map((row) => ({
        id: Number(row.dataset.variantId),
        quantity: 1,
        properties: {
          _complete_set: this.dataset.sectionId || 'complete-your-set',
        },
      }));
    const variantIdsToRemove = this.rows
      .filter((row) => row.querySelector('[data-complete-set-checkbox]')?.disabled !== true)
      .map((row) => String(row.dataset.variantId || ''))
      .filter((variantId) => variantId !== '')
      .filter((variantId) => this.cartVariantIds.has(variantId))
      .filter((variantId) => !selectedVariantIds.has(variantId));

    if (!this.elements.addButton) return;

    this.clearError();
    this.elements.addButton.disabled = true;
    this.elements.addButton.textContent = this.addingLabel;

    try {
      if (variantIdsToRemove.length) {
        await this.removeVariantsFromCart(variantIdsToRemove);
      }

      if (itemsToAdd.length) {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: itemsToAdd }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw result;
        }
      }

      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((cartResponse) =>
        cartResponse.json()
      );
      this.setCartVariantIds(cart);
      this.syncRowsWithCart({ preserveUserSelection: true });

      this.dispatchEvent(
        new CartUpdateEvent(cart, this.dataset.sectionId || 'complete-your-set', {
          source: 'complete-set-bundle',
          itemCount: cart.item_count,
          variantId: [...itemsToAdd.map((item) => item.id), ...variantIdsToRemove].join(','),
        })
      );

      this.elements.addButton.textContent = this.addedLabel;
      window.setTimeout(() => {
        if (this.elements.addButton) {
          this.elements.addButton.textContent = this.buttonLabel;
          this.elements.addButton.disabled = false;
        }
      }, 1400);
    } catch (error) {
      const message = error?.description || error?.message || 'Unable to add selected items. Please try again.';
      this.showError(message);
      this.dispatchEvent(
        new CartErrorEvent(this.dataset.sectionId || 'complete-your-set', message, error?.description, error?.errors)
      );

      this.elements.addButton.textContent = this.buttonLabel;
      this.elements.addButton.disabled = false;
    }
  };

  get selectedRows() {
    return this.rows.filter((row) => row.querySelector('[data-complete-set-checkbox]')?.checked);
  }

  async hydrateFromCart() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) =>
        response.json()
      );

      this.setCartVariantIds(cart);
      this.syncRowsWithCart();
    } catch (_error) {
      // Keep the Liquid-rendered defaults if the cart cannot be read.
    }
  }

  setCartVariantIds(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    this.cartLineKeysByVariantId = new Map();
    this.cartVariantIds = new Set(
      items
        .map((item) => {
          const variantId = String(item.variant_id || item.id || '');
          const lineKey = String(item.key || '');

          if (variantId && lineKey) {
            const lineKeys = this.cartLineKeysByVariantId.get(variantId) || [];
            lineKeys.push(lineKey);
            this.cartLineKeysByVariantId.set(variantId, lineKeys);
          }

          return variantId;
        })
        .filter((variantId) => variantId !== '')
    );
  }

  async removeVariantsFromCart(variantIds) {
    const lineKeys = variantIds.flatMap((variantId) => this.cartLineKeysByVariantId.get(String(variantId)) || []);

    for (const lineKey of lineKeys) {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw await response.json();
      }
    }
  }

  syncRowsWithCart({ preserveUserSelection = false } = {}) {
    this.rows.forEach((row) => {
      const select = row.querySelector('[data-complete-set-variant-select]');

      if (select) {
        this.selectCartVariant(row, select);
      }

      this.syncRowCheckboxWithCart(row, { preserveUserSelection });
    });

    this.update();
  }

  selectCartVariant(row, select) {
    const currentOption = select.selectedOptions?.[0];

    if (currentOption && this.cartVariantIds.has(String(currentOption.value))) {
      this.applyVariantOption(row, currentOption);
      return;
    }

    const cartOption = Array.from(select.options).find((option) => this.cartVariantIds.has(String(option.value)));

    if (!cartOption) return;

    select.value = cartOption.value;
    this.applyVariantOption(row, cartOption);
  }

  applyVariantOption(row, selectedOption) {
    row.dataset.variantId = selectedOption.value;
    row.dataset.priceCents = selectedOption.dataset.priceCents || '0';
    row.dataset.available = selectedOption.dataset.available || 'false';
    row.dataset.imageUrl = selectedOption.dataset.imageUrl || row.dataset.imageUrl || '';

    const price = row.querySelector('[data-complete-set-row-price]');
    if (price) {
      price.textContent = selectedOption.dataset.price || this.format(Number(row.dataset.priceCents || 0));
    }
  }

  syncRowCheckboxWithCart(row, { preserveUserSelection = false } = {}) {
    const checkbox = row.querySelector('[data-complete-set-checkbox]');
    if (!checkbox || checkbox.disabled) return;

    if (this.cartVariantIds.has(String(row.dataset.variantId || ''))) {
      checkbox.checked = true;
      row.dataset.cartMatched = 'true';
      return;
    }

    row.dataset.cartMatched = 'false';

    if (preserveUserSelection && row.dataset.userTouched === 'true') {
      return;
    }

    checkbox.checked = row.dataset.defaultChecked === 'true';
  }

  update() {
    const selectedRows = this.selectedRows;
    const selectedCount = selectedRows.length;
    const subtotal = selectedRows.reduce((sum, row) => sum + Number(row.dataset.priceCents || 0), 0);
    const currentTier = this.getCurrentTier(selectedCount);
    const nextTier = this.getNextTier(selectedCount);
    const discountRate = currentTier?.discount || 0;
    const discountAmount = Math.round((subtotal * discountRate) / 100);
    const total = Math.max(subtotal - discountAmount, 0);

    this.rows.forEach((row) => {
      row.classList.toggle('is-selected', row.querySelector('[data-complete-set-checkbox]')?.checked);
    });

    this.updateTiers(selectedCount, currentTier);
    this.updateSummary(selectedCount, subtotal, discountRate, discountAmount, total);
    this.updateNextCallout(selectedCount, subtotal, discountRate, nextTier);
    this.updateButton(selectedRows);
  }

  updateTiers(selectedCount, currentTier) {
    this.tiers.forEach((tier) => {
      const isUnlocked = selectedCount >= tier.threshold;
      const isActive = currentTier?.threshold === tier.threshold;
      const missingCount = Math.max(tier.threshold - selectedCount, 0);

      tier.element.classList.toggle('is-active', isActive);
      tier.element.classList.toggle('is-unlocked', isUnlocked && !isActive);
      tier.element.classList.toggle('is-locked', !isUnlocked);

      if (!tier.message) return;

      if (isActive && tier.discount > 0) {
        tier.message.textContent = 'You are saving';
      } else if (isActive) {
        tier.message.textContent = 'Start building your set';
      } else if (isUnlocked) {
        tier.message.textContent = 'Unlocked';
      } else {
        tier.message.textContent = `Add ${missingCount} more ${this.pluralize('item', missingCount)}`;
      }
    });
  }

  updateSummary(selectedCount, subtotal, discountRate, discountAmount, total) {
    if (this.elements.count) {
      this.elements.count.textContent = `${selectedCount} ${this.pluralize('item', selectedCount)} selected`;
    }

    if (this.elements.unlock) {
      this.elements.unlock.textContent =
        discountRate > 0 ? `You are unlocking ${discountRate}% OFF` : 'Add more items to unlock savings';
    }

    if (this.elements.subtotalLabel) {
      this.elements.subtotalLabel.textContent = `Subtotal (${selectedCount} ${this.pluralize('item', selectedCount)})`;
    }

    if (this.elements.discountLabel) {
      this.elements.discountLabel.textContent = `Bundle discount (${discountRate}%)`;
    }

    if (this.elements.subtotal) this.elements.subtotal.textContent = this.format(subtotal);
    if (this.elements.discount) this.elements.discount.textContent = `- ${this.format(discountAmount)}`;
    if (this.elements.save) this.elements.save.textContent = this.format(discountAmount);
    if (this.elements.total) this.elements.total.textContent = this.format(total);
  }

  updateNextCallout(selectedCount, subtotal, discountRate, nextTier) {
    if (!this.elements.next) return;

    if (!nextTier) {
      this.elements.next.hidden = true;
      return;
    }

    const missingCount = Math.max(nextTier.threshold - selectedCount, 0);
    const suggestedRows = this.rows.filter((row) => !row.querySelector('[data-complete-set-checkbox]')?.checked);
    const suggestedRow = suggestedRows[0];
    const suggestedPrice = Number(suggestedRow?.dataset.priceCents || 0);
    const estimatedSubtotal = subtotal + suggestedPrice * missingCount;
    const currentDiscountAmount = Math.round((subtotal * discountRate) / 100);
    const nextDiscountAmount = Math.round((estimatedSubtotal * nextTier.discount) / 100);
    const extraSavings = Math.max(nextDiscountAmount - currentDiscountAmount, 0);

    if (this.elements.nextTitle) {
      this.elements.nextTitle.textContent = `Add ${missingCount} more ${this.pluralize('item', missingCount)}`;
    }

    if (this.elements.nextDiscount) {
      this.elements.nextDiscount.textContent = `Unlock ${nextTier.discount}% OFF`;
    }

    if (this.elements.nextSave) {
      this.elements.nextSave.textContent = extraSavings > 0 ? `Save an extra ${this.format(extraSavings)}` : 'Unlock more savings';
    }

    if (this.elements.nextImage) {
      const imageUrl = suggestedRow?.dataset.imageUrl;
      this.elements.nextImage.hidden = !imageUrl;
      if (imageUrl) {
        this.elements.nextImage.style.backgroundImage = `url("${imageUrl}")`;
      } else {
        this.elements.nextImage.style.backgroundImage = '';
      }
    }

    this.elements.next.hidden = false;
  }

  updateButton(selectedRows) {
    if (!this.elements.addButton) return;

    const hasUnavailable = selectedRows.some((row) => row.dataset.available !== 'true');
    const canAdd = selectedRows.length > 0 && !hasUnavailable;

    this.elements.addButton.disabled = !canAdd;
  }

  getCurrentTier(selectedCount) {
    return this.tiers
      .filter((tier) => selectedCount >= tier.threshold)
      .sort((a, b) => b.threshold - a.threshold)[0];
  }

  getNextTier(selectedCount) {
    return this.tiers
      .filter((tier) => selectedCount < tier.threshold)
      .sort((a, b) => a.threshold - b.threshold)[0];
  }

  format(cents) {
    return formatMoney(cents, this.moneyFormat, this.currencyCode);
  }

  pluralize(word, count) {
    return count === 1 ? word : `${word}s`;
  }

  clearError() {
    if (!this.elements.error) return;

    this.elements.error.hidden = true;
    this.elements.error.textContent = '';
  }

  showError(message) {
    if (!this.elements.error) return;

    this.elements.error.hidden = false;
    this.elements.error.textContent = message;
  }
}

if (!customElements.get('complete-set-bundle')) {
  customElements.define('complete-set-bundle', CompleteSetBundle);
}
